// CatalogSuggestionsTab — Vorschläge-Reiter des Karten-Katalogs: Themen-Zeile des Decks
// (`SuggestionThemeRow`), Filterzeile, Rangliste der passenden Karten. Die Reihenfolge IST
// das Ergebnis der Vorschlags-Maschine — deshalb hat dieser Reiter kein Sortier-Menü.
import React from 'react';
import CardGrid from '../../../ui/CardGrid';
import Divider from '../../../ui/Divider';
import { useLoadMoreSentinel } from '../../../hooks/useLoadMoreSentinel';
import CardBrowserFilterRow from '../components/CardBrowserFilterRow';
import CatalogCardTile from './CatalogCardTile';
import CatalogSkeletonGrid from './CatalogSkeletonGrid';
import SuggestionListState from './SuggestionListState';
import SuggestionThemeRow from './SuggestionThemeRow';
import { popularDividerIndex } from './profileThemes';
import { useDeckSuggestions } from './useDeckSuggestions';
import { useSuggestionSettings } from './useSuggestionSettings';

/**
 * @param {number} deckId — numerische ID des virtuellen Decks
 * @param {object} filters — Filter-Objekt; liegt in `CardCatalog`, weil `Tabs.Panel` den
 *   inaktiven Reiter abbaut
 * @param {(patch: object) => void} onFiltersChange — Teil-Änderung des Filter-Objekts
 * @param {boolean} canAdd — Plus-Knöpfe zeigen (eigenes, bearbeitbares Deck)
 * @param {string|null} addingId — Scryfall-ID der Karte, deren Add gerade läuft
 * @param {(entry) => void} onAdd — Plus-Klick
 * @param {(entry) => void} onOpenCard — Klick auf die Kachel (Karten-Detail)
 * @param {boolean} dndEnabled — Kacheln als Drag-Quellen freigeben
 */
export default function CatalogSuggestionsTab({
  deckId,
  filters,
  onFiltersChange,
  canAdd = false,
  addingId = null,
  onAdd,
  onOpenCard,
  dndEnabled = false,
}) {
  const {
    profile,
    entries,
    available,
    status,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDeckSuggestions(deckId, { filters });

  const sentinelRef = useLoadMoreSentinel({ hasNextPage, isFetchingNextPage, fetchNextPage });

  const {
    hiddenCards,
    hideCard,
    hidingOracleIds,
    unhideCard,
    muteTag,
    unmuteTag,
  } = useSuggestionSettings(deckId);

  const trennIndex = popularDividerIndex(entries);

  return (
    <div className="card-catalog__tab">
      <SuggestionThemeRow
        profile={profile}
        entries={entries}
        hiddenCards={hiddenCards}
        unhideCard={unhideCard}
        muteTag={muteTag}
        unmuteTag={unmuteTag}
        showProfile={status === 'success' && available !== false}
      />

      <CardBrowserFilterRow
        filters={filters}
        onChange={onFiltersChange}
        showSort={false}
        showOwnedOnly
        mobileDock={false}
      />

      <SuggestionListState
        status={status}
        error={error}
        available={available}
        isEmpty={entries.length === 0}
        onRetry={() => refetch()}
        skeleton={<CatalogSkeletonGrid label="Vorschläge werden geladen" />}
      >
        <CardGrid variant="browse">
          {entries.map((eintrag, index) => (
            <React.Fragment key={eintrag.id}>
              {index === trennIndex && (
                <CardGrid.Full>
                  <Divider>Weitere beliebte Karten</Divider>
                </CardGrid.Full>
              )}
              <CatalogCardTile
                entry={eintrag}
                canAdd={canAdd}
                adding={addingId === eintrag.id}
                onAdd={onAdd}
                onOpen={onOpenCard}
                dndEnabled={dndEnabled}
                reasons={eintrag.reasons}
                onMuteTag={muteTag}
                onHide={(karte) => hideCard(karte.oracleId)}
                hiding={hidingOracleIds.has(eintrag.oracleId)}
              />
            </React.Fragment>
          ))}
        </CardGrid>
        <div ref={sentinelRef} className="card-catalog__sentinel" aria-hidden />
      </SuggestionListState>
    </div>
  );
}
